import { useMemo } from 'react';
import { useQueries, type UseQueryResult } from 'react-query';

import { apiUrl } from 'src/core/utils/api';

import { type Setting } from '../setting';

export type CostCenterReplacePreview = {
  companyId: string;
  costCenterIds: string[];
  preview: {
    approvedRequestsCount: number;
    notReadyPaymentsCount: number;
    pendingRequestsCount: number;
    purchaseOrdersCount: number;
    readyOrExportedPaymentsCount: number;
    subscriptionsCount: number;
    usersCount: number;
  };
};

export const useCostCenterReplacePreviewByCompany = (settings: Setting[]) => {
  const queryResults = useQueries(
    settings.flatMap((setting) => {
      const isEnabled = setting.entities && setting.entities.length > 0;
      const entities = setting.entities ?? [];

      return entities.map((entity) => ({
        enabled: isEnabled,
        queryKey: [
          entity.companyId,
          'cost-centers',
          setting.id,
          'replace',
          'preview',
          entity.costCenterId,
        ],
        queryFn: async (): Promise<CostCenterReplacePreview> => {
          const response = await fetch(
            apiUrl(
              `/cost-centers/${entity.costCenterId}/replace/preview`,
              entity.companyId,
            ),
            {
              method: 'GET',
              credentials: 'include',
              headers: {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                'Content-Type': 'application/json',
              },
            },
          );
          return {
            companyId: entity.companyId,
            costCenterIds: [entity.costCenterId],
            preview: await response.json(),
          };
        },
      }));
    }),
  ) as UseQueryResult<CostCenterReplacePreview, unknown>[];

  const aggregatedData = useMemo(() => {
    const companyMap = new Map<string, CostCenterReplacePreview>();

    queryResults.forEach((queryResult) => {
      if (queryResult.data) {
        const preview = queryResult.data;
        if (companyMap.has(preview.companyId)) {
          const existingPreview = companyMap.get(preview.companyId);
          if (existingPreview) {
            companyMap.set(preview.companyId, {
              ...existingPreview,
              costCenterIds: [
                ...existingPreview.costCenterIds,
                ...preview.costCenterIds,
              ],
              preview: {
                approvedRequestsCount:
                  existingPreview.preview.approvedRequestsCount +
                  preview.preview.approvedRequestsCount,
                notReadyPaymentsCount:
                  existingPreview.preview.notReadyPaymentsCount +
                  preview.preview.notReadyPaymentsCount,
                pendingRequestsCount:
                  existingPreview.preview.pendingRequestsCount +
                  preview.preview.pendingRequestsCount,
                purchaseOrdersCount:
                  existingPreview.preview.purchaseOrdersCount +
                  preview.preview.purchaseOrdersCount,
                readyOrExportedPaymentsCount:
                  existingPreview.preview.readyOrExportedPaymentsCount +
                  preview.preview.readyOrExportedPaymentsCount,
                subscriptionsCount:
                  existingPreview.preview.subscriptionsCount +
                  preview.preview.subscriptionsCount,
                usersCount:
                  existingPreview.preview.usersCount +
                  preview.preview.usersCount,
              },
            });
          }
        } else {
          companyMap.set(preview.companyId, { ...preview });
        }
      }
    });

    return Array.from(companyMap.values());
  }, [queryResults]);

  const isLoading = queryResults.some((query) => query.isLoading);
  const isError = queryResults.some((query) => query.isError);
  const isSuccess = queryResults.every((query) => query.isSuccess);
  const error = queryResults.find((query) => query.error)?.error;

  return { data: aggregatedData, isLoading, isError, isSuccess, error };
};

export const fetchCompaniesToReassign = async (
  unassignedCompanies: { costCenterId: string; companyId: string }[],
): Promise<CostCenterReplacePreview[]> => {
  const individualPreviews = await Promise.all<CostCenterReplacePreview>(
    unassignedCompanies.map(async (entity) => {
      const response = await fetch(
        apiUrl(
          `/cost-centers/${entity.costCenterId}/replace/preview`,
          entity.companyId,
        ),
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'Content-Type': 'application/json',
          },
        },
      );
      return {
        companyId: entity.companyId,
        costCenterIds: [entity.costCenterId],
        preview: await response.json(),
      };
    }),
  );

  const companyMap = new Map<string, CostCenterReplacePreview>();

  individualPreviews.forEach((preview) => {
    if (companyMap.has(preview.companyId)) {
      const existingPreview = companyMap.get(preview.companyId);
      if (existingPreview) {
        companyMap.set(preview.companyId, {
          ...existingPreview,
          costCenterIds: [
            ...existingPreview.costCenterIds,
            ...preview.costCenterIds,
          ],
          preview: {
            approvedRequestsCount:
              existingPreview.preview.approvedRequestsCount +
              preview.preview.approvedRequestsCount,
            notReadyPaymentsCount:
              existingPreview.preview.notReadyPaymentsCount +
              preview.preview.notReadyPaymentsCount,
            pendingRequestsCount:
              existingPreview.preview.pendingRequestsCount +
              preview.preview.pendingRequestsCount,
            purchaseOrdersCount:
              existingPreview.preview.purchaseOrdersCount +
              preview.preview.purchaseOrdersCount,
            readyOrExportedPaymentsCount:
              existingPreview.preview.readyOrExportedPaymentsCount +
              preview.preview.readyOrExportedPaymentsCount,
            subscriptionsCount:
              existingPreview.preview.subscriptionsCount +
              preview.preview.subscriptionsCount,
            usersCount:
              existingPreview.preview.usersCount + preview.preview.usersCount,
          },
        });
      }
    } else {
      companyMap.set(preview.companyId, { ...preview });
    }
  });

  const aggregatedPreviews = Array.from(companyMap.values());

  const companiesToReassign: CostCenterReplacePreview[] = [];
  aggregatedPreviews.forEach((p) => {
    if (Object.values(p.preview).some((count) => count > 0)) {
      companiesToReassign.push(p);
    }
  });
  return companiesToReassign;
};
